import type { DocumentNode, FragmentDefinitionNode, SelectionSetNode } from 'graphql';
import { Kind, OperationTypeNode } from 'graphql';

/**
 * Код отказа шлюза, когда капча (или App Check вместо неё) не прошла.
 * Шлюз: `PayloadException.code(CAPTCHA_ERROR)` в `GoogleReCaptchaClient`.
 */
export const CAPTCHA_ERROR_CODE = 'captcha.error';

/**
 * Поля шлюза, на которых проверяется капча. Только здесь токен App Check что-то значит, и
 * только здесь его стоит ждать.
 *
 * Откуда список. В teamplay-graphql капчу проверяет `GoogleReCaptchaClient.validateCaptcha`, и
 * зовёт его ровно одно место: `RequestContextInjector`, для аргумента с
 * `@Context(validateCaptcha = true)`. На 15.09.2026 такой аргумент есть у одиннадцати полей:
 * - `AuthorizationApi`: register, deleteMe, changeMail, login, changePassword, forgotPassword,
 *   resetPassword, beta.joinBetaRequest, beta.joinBetaRegister;
 * - `FeedbackApi`: addComplaint, addFeedback.
 * Сверить заново: `git grep "validateCaptcha = true"` в teamplay-graphql.
 *
 * Путь собран из имён полей схемы от корня мутации, а не из имени операции во фронте. Имя
 * операции придумывает автор документа, поле задаёт шлюз, поэтому новый документ с
 * `auth { login }` под любым именем опознаётся сам.
 *
 * Новое поле в `auth`, `auth.beta` или `feedback` роняет тест
 * `__tests__/captchaOperations.test.ts`, пока его не внесут сюда или в список полей без капчи.
 * Капча на поле в другом контейнере тест не заметит. Её ловит `gqlFetcher`: в приложении отказ
 * `captcha.error` на операции вне списка уходит в Sentry и повторяется один раз с токеном.
 */
export const CAPTCHA_FIELD_PATHS: ReadonlySet<string> = new Set([
  'auth.register',
  'auth.deleteMe',
  'auth.changeMail',
  'auth.login',
  'auth.changePassword',
  'auth.forgotPassword',
  'auth.resetPassword',
  'auth.beta.joinBetaRequest',
  'auth.beta.joinBetaRegister',
  'feedback.addComplaint',
  'feedback.addFeedback',
]);

// Самый длинный путь в списке – три поля (`auth.beta.joinBetaRequest`). Глубже смотреть незачем.
const MAX_DEPTH = 3;

type Walk = {
  fragments: ReadonlyMap<string, FragmentDefinitionNode>;
  // Фрагмент, уже пройденный на этом документе, второй раз не разбираем.
  seenFragments: Set<string>;
};

function selectsCaptchaField(
  selectionSet: SelectionSetNode,
  prefix: string,
  depth: number,
  walk: Walk
): boolean {
  for (const selection of selectionSet.selections) {
    if (selection.kind === Kind.FIELD) {
      // Имя поля, а не псевдоним: `signIn: login(...)` – всё тот же login.
      const path = prefix ? `${prefix}.${selection.name.value}` : selection.name.value;

      if (CAPTCHA_FIELD_PATHS.has(path)) {
        return true;
      }

      if (
        selection.selectionSet &&
        depth + 1 < MAX_DEPTH &&
        selectsCaptchaField(selection.selectionSet, path, depth + 1, walk)
      ) {
        return true;
      }
    } else if (selection.kind === Kind.INLINE_FRAGMENT) {
      if (selectsCaptchaField(selection.selectionSet, prefix, depth, walk)) {
        return true;
      }
    } else if (selection.kind === Kind.FRAGMENT_SPREAD) {
      const name = selection.name.value;
      const fragment = walk.fragments.get(name);

      if (fragment && !walk.seenFragments.has(name)) {
        walk.seenFragments.add(name);

        if (selectsCaptchaField(fragment.selectionSet, prefix, depth, walk)) {
          return true;
        }
      }
    }
  }

  return false;
}

// Документ строкой в проекте не встречается, но тип fetcher'а его допускает. Разбирать строку
// ради этого значило бы тащить парсер GraphQL в бандл, поэтому здесь грубая проверка: лишнее
// ожидание токена безвредно, а пропуск поймает страховка в gqlFetcher.
const CAPTCHA_CONTAINER_IN_TEXT = /\b(auth|feedback)\s*(\([^)]*\))?\s*\{/;

/**
 * Проверяется ли в этом документе капча, то есть нужен ли ему токен App Check.
 */
export function requiresCaptcha(document: DocumentNode | string): boolean {
  if (typeof document === 'string') {
    return CAPTCHA_CONTAINER_IN_TEXT.test(document);
  }

  const fragments = new Map<string, FragmentDefinitionNode>();

  for (const definition of document.definitions) {
    if (definition.kind === Kind.FRAGMENT_DEFINITION) {
      fragments.set(definition.name.value, definition);
    }
  }

  return document.definitions.some(
    (definition) =>
      definition.kind === Kind.OPERATION_DEFINITION &&
      definition.operation === OperationTypeNode.MUTATION &&
      selectsCaptchaField(definition.selectionSet, '', 0, { fragments, seenFragments: new Set() })
  );
}
